import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    // Invoice modal targets
    "invoiceLoading", "invoiceError", "invoiceErrorText", "invoiceEmpty",
    "invoiceList", "invoiceTableBody", "invoiceForm", "invoiceAmount",
    "invoicePaymentType", "selectedInvoiceLabel", "invoiceRemainingLabel",
    "invoiceSubmitBtn", "invoiceSubmitText", "invoiceSubmitSpinner",
    "invoiceSuccess", "invoiceSuccessText",
    // Appointment modal targets
    "appointmentLoading", "appointmentError", "appointmentErrorText",
    "appointmentEmpty", "appointmentList", "appointmentTableBody",
    "appointmentSubmitBtn", "appointmentSubmitText", "appointmentSubmitSpinner",
    "appointmentSuccess"
  ]

  static values = {
    unpaidInvoicesUrl: String,
    attachToInvoiceUrl: String,
    patientAppointmentsUrl: String,
    attachToAppointmentUrl: String
  }

  connect() {
    this.selectedMessageId = null
    this.selectedInvoiceId = null
    this.selectedInvoiceBalance = 0
    this.selectedAppointmentId = null
  }

  // Escape HTML to prevent XSS when injecting into innerHTML
  // Also escapes quotes for safe use inside HTML attributes
  _escapeHtml(str) {
    if (str == null) return ''
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  // ========== Invoice Modal ==========

  openInvoiceModal(event) {
    event.preventDefault()
    event.stopPropagation()

    this.selectedMessageId = event.currentTarget.dataset.messageId
    this._resetInvoiceModal()
    $('#mediaAttachInvoiceModal').modal('show')
    this._fetchUnpaidInvoices()
  }

  selectInvoice(event) {
    const row = event.currentTarget
    this.selectedInvoiceId = row.dataset.invoiceId
    this.selectedInvoiceBalance = parseInt(row.dataset.remainingBalance)

    // Highlight selected row
    this.invoiceTableBodyTarget.querySelectorAll('tr').forEach(r => r.classList.remove('table-active'))
    row.classList.add('table-active')

    // Show payment form
    const label = row.dataset.invoiceLabel
    this.selectedInvoiceLabelTarget.textContent = label
    this.invoiceRemainingLabelTarget.textContent = (this.selectedInvoiceBalance / 100.0).toFixed(2)
    this.invoiceAmountTarget.value = (this.selectedInvoiceBalance / 100.0).toFixed(2)
    this.invoiceAmountTarget.max = (this.selectedInvoiceBalance / 100.0).toFixed(2)

    this.invoiceFormTarget.classList.remove('d-none')
    this.invoiceSubmitBtnTarget.classList.remove('d-none')
  }

  async submitInvoiceAttachment() {
    if (!this.selectedInvoiceId || !this.selectedMessageId) return

    const amountFloat = parseFloat(this.invoiceAmountTarget.value)
    if (isNaN(amountFloat) || amountFloat <= 0) {
      this._showInvoiceError('Ingresá un monto válido mayor a cero')
      return
    }

    const amountCents = Math.round(amountFloat * 100)
    if (amountCents > this.selectedInvoiceBalance) {
      this._showInvoiceError(`El monto excede el saldo pendiente ($${(this.selectedInvoiceBalance / 100.0).toFixed(2)})`)
      return
    }

    this._setInvoiceSubmitting(true)

    try {
      const response = await fetch(this.attachToInvoiceUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          message_id: this.selectedMessageId,
          invoice_id: this.selectedInvoiceId,
          amount_cents: amountCents,
          payment_type: this.invoicePaymentTypeTarget.value
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        let msg = data.message || 'Pago registrado exitosamente'
        if (data.colppy_error) {
          msg += ` (Colppy: ${data.colppy_error})`
        }
        this._showInvoiceSuccess(msg)
        if (data.linked_invoice) {
          this._addBadgeToMessage(this.selectedMessageId, 'invoice', data.linked_invoice)
        }
      } else {
        this._showInvoiceError(data.error || 'Error al procesar el pago')
      }
    } catch (error) {
      this._showInvoiceError('Error de conexión. Intentá nuevamente.')
    } finally {
      this._setInvoiceSubmitting(false)
    }
  }

  // ========== Appointment Modal ==========

  openAppointmentModal(event) {
    event.preventDefault()
    event.stopPropagation()

    this.selectedMessageId = event.currentTarget.dataset.messageId
    this._resetAppointmentModal()
    $('#mediaAttachAppointmentModal').modal('show')
    this._fetchPatientAppointments()
  }

  selectAppointment(event) {
    const row = event.currentTarget
    this.selectedAppointmentId = row.dataset.appointmentId

    // Highlight selected row
    this.appointmentTableBodyTarget.querySelectorAll('tr').forEach(r => r.classList.remove('table-active'))
    row.classList.add('table-active')

    this.appointmentSubmitBtnTarget.classList.remove('d-none')
  }

  async submitAppointmentAttachment() {
    if (!this.selectedAppointmentId || !this.selectedMessageId) return

    this._setAppointmentSubmitting(true)

    try {
      const response = await fetch(this.attachToAppointmentUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          message_id: this.selectedMessageId,
          appointment_id: this.selectedAppointmentId
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this._showAppointmentSuccess()
        if (data.linked_appointment) {
          this._addBadgeToMessage(this.selectedMessageId, 'appointment', data.linked_appointment)
        }
      } else {
        this._showAppointmentError(data.error || 'Error al adjuntar comprobante')
      }
    } catch (error) {
      this._showAppointmentError('Error de conexión. Intentá nuevamente.')
    } finally {
      this._setAppointmentSubmitting(false)
    }
  }

  // ========== Private: Invoice ==========

  async _fetchUnpaidInvoices() {
    try {
      const response = await fetch(this.unpaidInvoicesUrlValue, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      this.invoiceLoadingTarget.classList.add('d-none')

      if (!response.ok) {
        this._showInvoiceError(data.error || 'Error al cargar facturas')
        return
      }

      if (data.invoices.length === 0) {
        this.invoiceEmptyTarget.classList.remove('d-none')
        return
      }

      this._renderInvoiceTable(data.invoices)
      this.invoiceListTarget.classList.remove('d-none')
    } catch (error) {
      this.invoiceLoadingTarget.classList.add('d-none')
      this._showInvoiceError('Error de conexión al cargar facturas')
    }
  }

  _renderInvoiceTable(invoices) {
    this.invoiceTableBodyTarget.innerHTML = invoices.map(inv => {
      const nro = this._escapeHtml(inv.colppy_nro_factura || '-')
      const desc = this._escapeHtml(inv.description || '-')
      const date = this._escapeHtml(inv.date_of_issue)
      const total = (inv.net_amount / 100.0).toFixed(2)
      const remaining = (inv.remaining_balance / 100.0).toFixed(2)
      const label = this._escapeHtml(inv.colppy_nro_factura ? `#${inv.colppy_nro_factura}` : `${inv.date_of_issue} - ${inv.description || '-'}`)
      const syncBadge = inv.synced_to_colppy
        ? '<span class="badge badge-success ml-1" title="Sincronizada con Colppy"><i class="fas fa-cloud"></i></span>'
        : ''

      return `<tr class="cursor-pointer" style="cursor:pointer;"
                  data-action="click->media-attach#selectInvoice"
                  data-invoice-id="${inv.id}"
                  data-remaining-balance="${inv.remaining_balance}"
                  data-invoice-label="${label}">
                <td>${date}</td>
                <td>${nro}${syncBadge}</td>
                <td class="text-truncate" style="max-width:200px;" title="${desc}">${desc}</td>
                <td class="text-right">$${total}</td>
                <td class="text-right"><strong>$${remaining}</strong></td>
              </tr>`
    }).join('')
  }

  _resetInvoiceModal() {
    this.selectedInvoiceId = null
    this.selectedInvoiceBalance = 0
    this.invoiceLoadingTarget.classList.remove('d-none')
    this.invoiceErrorTarget.classList.add('d-none')
    this.invoiceEmptyTarget.classList.add('d-none')
    this.invoiceListTarget.classList.add('d-none')
    this.invoiceFormTarget.classList.add('d-none')
    this.invoiceSubmitBtnTarget.classList.add('d-none')
    this.invoiceSuccessTarget.classList.add('d-none')
  }

  _showInvoiceError(msg) {
    this.invoiceErrorTextTarget.textContent = msg
    this.invoiceErrorTarget.classList.remove('d-none')
  }

  _showInvoiceSuccess(msg) {
    this.invoiceSuccessTextTarget.textContent = msg
    // Hide other sections
    this.invoiceListTarget.classList.add('d-none')
    this.invoiceFormTarget.classList.add('d-none')
    this.invoiceSubmitBtnTarget.classList.add('d-none')
    this.invoiceSuccessTarget.classList.remove('d-none')
  }

  _setInvoiceSubmitting(submitting) {
    this.invoiceSubmitBtnTarget.disabled = submitting
    this.invoiceSubmitTextTarget.classList.toggle('d-none', submitting)
    this.invoiceSubmitSpinnerTarget.classList.toggle('d-none', !submitting)
  }

  // ========== Private: Appointment ==========

  async _fetchPatientAppointments() {
    try {
      const response = await fetch(this.patientAppointmentsUrlValue, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      this.appointmentLoadingTarget.classList.add('d-none')

      if (!response.ok) {
        this._showAppointmentError(data.error || 'Error al cargar turnos')
        return
      }

      if (data.appointments.length === 0) {
        this.appointmentEmptyTarget.classList.remove('d-none')
        return
      }

      this._renderAppointmentTable(data.appointments)
      this.appointmentListTarget.classList.remove('d-none')
    } catch (error) {
      this.appointmentLoadingTarget.classList.add('d-none')
      this._showAppointmentError('Error de conexión al cargar turnos')
    }
  }

  _renderAppointmentTable(appointments) {
    const statusLabels = {
      'active': '<span class="badge badge-primary">Activo</span>',
      'completed': '<span class="badge badge-success">Completado</span>'
    }

    this.appointmentTableBodyTarget.innerHTML = appointments.map(apt => {
      const status = statusLabels[apt.status] || this._escapeHtml(apt.status)
      const price = apt.price != null ? `$${apt.price.toFixed(2)}` : '-'
      const receipt = apt.has_receipt
        ? '<span class="badge badge-success"><i class="fas fa-check"></i> Sí</span>'
        : '<span class="badge badge-light">No</span>'
      const title = this._escapeHtml(apt.title)
      const doctorName = this._escapeHtml(apt.doctor_name || '-')
      const startTime = this._escapeHtml(apt.start_time)

      return `<tr style="cursor:pointer;"
                  data-action="click->media-attach#selectAppointment"
                  data-appointment-id="${apt.id}">
                <td>${startTime}</td>
                <td class="text-truncate" style="max-width:150px;" title="${title}">${title}</td>
                <td>${doctorName}</td>
                <td>${status}</td>
                <td class="text-right">${price}</td>
                <td class="text-center">${receipt}</td>
              </tr>`
    }).join('')
  }

  _resetAppointmentModal() {
    this.selectedAppointmentId = null
    this.appointmentLoadingTarget.classList.remove('d-none')
    this.appointmentErrorTarget.classList.add('d-none')
    this.appointmentEmptyTarget.classList.add('d-none')
    this.appointmentListTarget.classList.add('d-none')
    this.appointmentSubmitBtnTarget.classList.add('d-none')
    this.appointmentSuccessTarget.classList.add('d-none')
  }

  _showAppointmentError(msg) {
    this.appointmentErrorTextTarget.textContent = msg
    this.appointmentErrorTarget.classList.remove('d-none')
  }

  _showAppointmentSuccess() {
    this.appointmentListTarget.classList.add('d-none')
    this.appointmentSubmitBtnTarget.classList.add('d-none')
    this.appointmentSuccessTarget.classList.remove('d-none')
  }

  _setAppointmentSubmitting(submitting) {
    this.appointmentSubmitBtnTarget.disabled = submitting
    this.appointmentSubmitTextTarget.classList.toggle('d-none', submitting)
    this.appointmentSubmitSpinnerTarget.classList.toggle('d-none', !submitting)
  }

  // ========== Private: Badge injection ==========

  _addBadgeToMessage(messageId, type, linked) {
    const messageEl = document.getElementById(`message_${messageId}`)
    if (!messageEl) return

    const label = this._escapeHtml(linked.label)
    const editPath = this._escapeHtml(linked.path)
    const icon = type === 'invoice' ? 'fa-file-invoice-dollar' : 'fa-calendar-check'
    const badgeClass = type === 'invoice' ? 'badge-invoice' : 'badge-appointment'

    const badgeHtml = `<a href="${editPath}" class="media-link-badge ${badgeClass}" target="_blank">` +
      `<i class="fas ${icon}"></i> ${label}</a>`

    // Find or create the badges container inside .media-info
    const mediaInfo = messageEl.querySelector('.media-info')
    if (!mediaInfo) return

    let badgesDiv = mediaInfo.querySelector('.media-link-badges')
    if (!badgesDiv) {
      badgesDiv = document.createElement('div')
      badgesDiv.className = 'media-link-badges'
      mediaInfo.appendChild(badgesDiv)
    }
    badgesDiv.insertAdjacentHTML('beforeend', badgeHtml)

    // Hide the corresponding "Vincular" menu item
    const menuKey = type === 'invoice' ? 'openInvoiceModal' : 'openAppointmentModal'
    messageEl.querySelectorAll(`[data-action*="media-attach#${menuKey}"]`).forEach(el => {
      el.style.display = 'none'
    })
  }
}
